import React, { useState, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { RiDeleteBin6Line, RiShoppingCartLine, RiArrowRightUpLine } from 'react-icons/ri';
import { AiOutlinePlus, AiOutlineMinus } from 'react-icons/ai';
import './Cart.css';
import { useCart } from '../../Context/CartContext';
import AuthContext from '../../Context/AuthContext';
import CustomModal from '../../Component/UI/CustomModal/customModal';
import Button from '../../Component/UI/Button/Button';
import { useAppLanguage } from '../../Hooks/useAppLanguage';
import { getCartLineId } from '../../Utils/cartItem';
import { usePublicSettings } from '../../Context/PublicSettingsContext';
import { SHIPPING_HANDLING } from '../../Utils/orderConfig';
import { getSizeLabel, getColorLabel } from '../../Utils/sizeColorI18n';
import { formatSelectedOptionsLabel } from '../../Utils/productVariants';
import { getCategoryLabel, getSubcategoryLabel } from '../../Utils/categoryI18n';
import ProductCardImage from '../../Component/UI/ProductCard/ProductCardImage';

const Cart = () => {
    const { t, selectedLanguage, dir, langPath } = useAppLanguage();
    const { formatPrice } = usePublicSettings();
    const {
        cartItems,
        removeFromCart,
        updateCartItemQuantity,
        cartRemainingTime,
        formatTime,
        getCartTotal,
    } = useCart();
    const { user } = useContext(AuthContext);
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [agreeTerms, setAgreeTerms] = useState(false);
    const [termsError, setTermsError] = useState('');
    const navigate = useNavigate();

    const subtotalAmount = getCartTotal();
    const shippingHandling = SHIPPING_HANDLING;
    const cartTotal = subtotalAmount + shippingHandling;

    const handleQuantityDecrease = (lineId, quantity) => {
        if (quantity <= 1) {
            removeFromCart(lineId);
            return;
        }
        updateCartItemQuantity(lineId, quantity - 1);
    };

    const getProductName = (item) =>
        item?.name?.[selectedLanguage] || item?.name?.en || item?.name || t('cart.unknownItem');

    const handleCheckout = () => {
        if (!agreeTerms) {
            setTermsError(t('cart.termsRequired'));
            return;
        }
        setTermsError('');

        if (!user?._id) {
            setModalIsOpen(true);
            return;
        }

        navigate(langPath(`/checkout/${user._id}`));
    };

    return (
        <main className="main">
            <section className="cart-page">
                <div className="container" dir={dir}>

                    {cartItems.length === 0 ? (
                        <div className="cart-empty">
                            <div className="cart-empty-visual" aria-hidden="true">
                                <RiShoppingCartLine className="cart-empty-icon" />
                            </div>
                            <h2 className="cart-empty-title">{t('cart.empty')}</h2>
                            <p className="cart-empty-text">{t('cart.emptyDescription')}</p>
                            <Link to={langPath('/products')} className="cart-btn cart-btn--primary">
                                <span>{t('cart.continueShopping')}</span>
                                <RiArrowRightUpLine className="cart-btn-ico" aria-hidden />
                                <span className="cart-btn-shine" aria-hidden />
                            </Link>
                        </div>
                    ) : (
                        <div className="cart-layout">
                            <div>
                                <p className="cart-line-summary" role="status">
                                    {t('cart.summary', { count: cartItems.length })}
                                </p>
                                <ul className="cart-list">
                                    {cartItems.map((item) => {
                                        const lineId = getCartLineId(item);
                                        const variantLabel =
                                            formatSelectedOptionsLabel(
                                                item.selectedOptions,
                                                getSizeLabel(item.size, selectedLanguage) || item.size,
                                                getColorLabel(item.color, selectedLanguage) || item.color
                                            ) || item.variantTitle;
                                        const categoryLabel = getCategoryLabel(item.category, selectedLanguage);
                                        const subCategoryLabel = getSubcategoryLabel(
                                            item.category,
                                            item.subCategory,
                                            selectedLanguage
                                        );

                                        const productId = item.productId || item._id;
                                        const detailPath = langPath(`/product-details/${productId}`);

                                        return (
                                            <li key={lineId}>
                                                <article className="cart-card">
                                                    <div className="cart-card-media">
                                                        <Link to={detailPath} className="cart-card-media-link">
                                                            <ProductCardImage
                                                                product={item}
                                                                productName={getProductName(item)}
                                                                alt={getProductName(item)}
                                                                className="cart-card-img"
                                                            />
                                                        </Link>
                                                    </div>
                                                    <div>
                                                        <h2 className="cart-card-title">
                                                            <Link to={detailPath} className="cart-card-title-link">
                                                                {getProductName(item)}
                                                            </Link>
                                                            {variantLabel ? (
                                                                <span className="cart-card-variant">({variantLabel})</span>
                                                            ) : null}
                                                        </h2>
                                                        <p className="cart-card-category">
                                                            {categoryLabel}
                                                            {subCategoryLabel ? ` / ${subCategoryLabel}` : ''}
                                                        </p>
                                                        <p className="cart-card-unit">{formatPrice(item.price)}</p>
                                                    </div>
                                                    <div className="cart-card-qty" role="group" aria-label={t('cart.quantity')}>
                                                        <button
                                                            type="button"
                                                            className="cart-qty-btn"
                                                            onClick={() => handleQuantityDecrease(lineId, item.quantity)}
                                                            aria-label={t('cart.decreaseQuantity')}
                                                        >
                                                            <AiOutlineMinus aria-hidden />
                                                        </button>
                                                        <span className="cart-qty-value">{item.quantity}</span>
                                                        <button
                                                            type="button"
                                                            className="cart-qty-btn"
                                                            onClick={() => updateCartItemQuantity(lineId, item.quantity + 1)}
                                                            aria-label={t('cart.increaseQuantity')}
                                                        >
                                                            <AiOutlinePlus aria-hidden />
                                                        </button>
                                                    </div>
                                                    <div className="cart-card-total">
                                                        <span className="cart-card-line-total">
                                                            {formatPrice(item.price * item.quantity)}
                                                        </span>
                                                        <button
                                                            type="button"
                                                            className="cart-card-remove"
                                                            onClick={() => removeFromCart(lineId)}
                                                            aria-label={t('cart.remove')}
                                                        >
                                                            <RiDeleteBin6Line aria-hidden />
                                                        </button>
                                                    </div>
                                                </article>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>

                            <aside className="cart-summary-panel">
                                <h2 className="cart-summary-heading">{t('cart.orderSummary')}</h2>
                                <div className="cart-summary-rows">
                                    <div className="cart-summary-row">
                                        <span>{t('cart.subtotal')}</span>
                                        <span>{formatPrice(subtotalAmount)}</span>
                                    </div>
                                    <div className="cart-summary-row">
                                        <span>{t('cart.shipping')}</span>
                                        <span>{formatPrice(shippingHandling)}</span>
                                    </div>
                                    <div className="cart-summary-row cart-summary-row--total">
                                        <span>{t('cart.total')}</span>
                                        <span>{formatPrice(cartTotal)}</span>
                                    </div>
                                </div>

                                {cartRemainingTime > 0 && (
                                    <div className="cart-expiry-notice" role="status">
                                        {t('cart.expiresIn')} {formatTime(cartRemainingTime)}
                                    </div>
                                )}

                                <label className="cart-terms">
                                    <input
                                        type="checkbox"
                                        checked={agreeTerms}
                                        onChange={(e) => {
                                            setAgreeTerms(e.target.checked);
                                            if (e.target.checked) setTermsError('');
                                        }}
                                    />
                                    <span>
                                        {t('cart.agreeTerms')}{' '}
                                        <Link to={langPath('/contact')} className="cart-terms-link">
                                            {t('cart.termsLink')}
                                        </Link>
                                    </span>
                                </label>
                                {termsError && <p className="cart-terms-error">{termsError}</p>}

                                <button
                                    type="button"
                                    className="cart-btn cart-btn--primary cart-btn--block"
                                    onClick={handleCheckout}
                                >
                                    <span>{t('cart.proceedCheckout')}</span>
                                    <RiArrowRightUpLine className="cart-btn-ico" aria-hidden />
                                    <span className="cart-btn-shine" aria-hidden />
                                </button>

                                <div className="cart-summary-foot">
                                    <Link to={langPath('/products')} className="cart-foot-link cart-foot-link--strong">
                                        {t('cart.continueShopping')}
                                    </Link>
                                    {(process.env.REACT_APP_SHOW_TRACK_ORDERS_LINKS || 'true').toLowerCase() === 'true' && (
                                        <Link to={langPath('/track-order')} className="cart-foot-link">
                                            {t('cart.trackOrders')}
                                        </Link>
                                    )}
                                </div>
                            </aside>
                        </div>
                    )}
                </div>
            </section>

            <CustomModal setIsModalOpen={setModalIsOpen} isModalOpen={modalIsOpen} type="confirm">
                <div>
                    <h3>{t('cart.loginRequired')}</h3>
                </div>
                <div>
                    <p>{t('cart.loginRequiredBody')}</p>
                </div>
                <div>
                    <Button variant="outline" onClick={() => setModalIsOpen(false)}>
                        {t('cart.notNow')}
                    </Button>
                    <Button
                        flex
                        onClick={() => {
                            setModalIsOpen(false);
                            navigate(langPath('/sign-in'));
                        }}
                    >
                        {t('cart.loginNow')}
                    </Button>
                </div>
            </CustomModal>
        </main>
    );
};

export default Cart;
